import { DialogActions, Link, Stack, Typography } from "@mui/material";
import { useSetStardustParticipationMutation } from "Api/Mutations/Loyalty/useSetStardustParticipationMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { useStardustTermsUrl } from "Components/Loyalty/Stardust/useStardustTermsUrl";
import {
  ProgramParticipationSource,
  ProgramParticipationState,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useNavigate } from "Navigation/useNavigate";
import { toast } from "sonner";

const RESOURCES = Resources.Pages.Content.Stardust.Consent;

/**
 * Blocking consent dialog of the Stardust page (ZS-1624, legal 2026-09-29): a person without
 * consent gets no Stardust content until they agree here; "Back" (and closing) returns to the
 * dashboard. The agreement is recorded with its own source, so the history tells this dialog apart
 * from the wizard checkbox.
 */
export const StardustConsentDialog = () => {
  const { t } = useResource();
  const navigate = useNavigate();
  const termsUrl = useStardustTermsUrl();
  const { mutate: giveConsent, isPending } =
    useSetStardustParticipationMutation({
      onError: () => toast.error(t(Resources.Common.SaveError)),
    });

  const goBack = () => navigate(appUrl("dashboard"));

  return (
    <ModalDialog isOpen onClose={goBack} title={t(RESOURCES.Title)}>
      <Stack gap={2}>
        <Typography>{t(RESOURCES.Description)}</Typography>
        <Link target="_blank" rel="noopener noreferrer" href={termsUrl}>
          {t(RESOURCES.TermsLink)}
        </Link>
      </Stack>
      <DialogActions sx={{ px: 0 }}>
        <PrimaryButton
          size="small"
          variant="text"
          color="info"
          onClick={goBack}
          disabled={isPending}
        >
          {t(Resources.Common.Back)}
        </PrimaryButton>
        <PrimaryButton
          size="small"
          color="info"
          isLoading={isPending}
          onClick={() =>
            giveConsent({
              state: ProgramParticipationState.OptedIn,
              source: ProgramParticipationSource.ConsentDialog,
            })
          }
        >
          {t(RESOURCES.Confirm)}
        </PrimaryButton>
      </DialogActions>
    </ModalDialog>
  );
};
